import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  TruckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';

interface SaleData {
  sellingPrice: number;
  soldDate: string;
  paymentMethod: 'cash' | 'financing' | 'exchange';
  notes?: string;
}

const VehicleSale: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: vehicle, isLoading } = useQuery(
    ['vehicle', id],
    () => api.get(`/vehicles/${id}`).then(res => res.data),
    { enabled: !!id }
  );

  const [saleData, setSaleData] = useState<SaleData>({
    sellingPrice: 0,
    soldDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    notes: '',
  });

  const [sellingPriceDisplay, setSellingPriceDisplay] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Inicializar dados quando o veículo for carregado
  React.useEffect(() => {
    if (vehicle) {
      const initialPrice = vehicle.sellingPrice || vehicle.purchasePrice;
      setSaleData(prev => ({
        ...prev,
        sellingPrice: initialPrice,
      }));
      setSellingPriceDisplay(
        initialPrice.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    }
  }, [vehicle]);

  const sellVehicleMutation = useMutation(
    (data: SaleData) => api.put(`/vehicles/${id}/sell`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehicles']);
        queryClient.invalidateQueries(['vehicle', id]);
        navigate(`/vehicles/${id}`);
      },
      onError: (error: any) => {
        console.error('Erro ao vender veículo:', error);
      },
    }
  );

  const formatDisplayCurrency = (value: string): string => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';

    const numberValue = parseInt(numericValue, 10) / 100;
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSellingPriceChange = (value: string) => {
    const formatted = formatDisplayCurrency(value);
    setSellingPriceDisplay(formatted);

    const numericValue = parseFloat(formatted.replace(/\./g, '').replace(',', '.')) || 0;
    setSaleData(prev => ({ ...prev, sellingPrice: numericValue }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!saleData.sellingPrice || saleData.sellingPrice <= 0) {
      newErrors.sellingPrice = 'Preço de venda é obrigatório e deve ser maior que zero';
    }

    if (!saleData.soldDate) {
      newErrors.soldDate = 'Data da venda é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      sellVehicleMutation.mutate(saleData);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dados do veículo...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Veículo não encontrado</p>
          <button
            onClick={() => navigate('/vehicles')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            Voltar para veículos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/vehicles/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para detalhes do veículo
          </button>

          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TruckIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h1 className="text-3xl font-bold text-gray-900">Vender Veículo</h1>
              <p className="text-gray-600">
                {vehicle.make} {vehicle.model} {vehicle.year} - {vehicle.licensePlate}
              </p>
            </div>
          </div>
        </div>

        {/* Informações do Veículo */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Informações do Veículo</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Veículo</label>
              <p className="text-gray-900 font-medium">
                {vehicle.make} {vehicle.model} {vehicle.year}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Placa</label>
              <p className="text-gray-900 font-medium">{vehicle.licensePlate}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
              <p className="text-gray-900">{vehicle.color}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço de Compra
              </label>
              <p className="text-gray-900 font-medium">{formatCurrency(vehicle.purchasePrice)}</p>
            </div>
          </div>
        </div>

        {/* Formulário de Venda */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 mr-2 text-green-600" />
            Dados da Venda
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Preço de Venda */}
              <div>
                <label
                  htmlFor="sellingPrice"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Preço de Venda *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">R$</span>
                  </div>
                  <input
                    id="sellingPrice"
                    type="text"
                    value={sellingPriceDisplay}
                    onChange={e => handleSellingPriceChange(e.target.value)}
                    className={`w-full pl-12 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.sellingPrice ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.sellingPrice && (
                  <p className="mt-1 text-sm text-red-600">{errors.sellingPrice}</p>
                )}
              </div>

              {/* Data da Venda */}
              <div>
                <label htmlFor="soldDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Data da Venda *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="soldDate"
                    type="date"
                    value={saleData.soldDate}
                    onChange={e => setSaleData(prev => ({ ...prev, soldDate: e.target.value }))}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.soldDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.soldDate && <p className="mt-1 text-sm text-red-600">{errors.soldDate}</p>}
              </div>
            </div>

            {/* Forma de Pagamento */}
            <div>
              <label
                htmlFor="paymentMethod"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Forma de Pagamento
              </label>
              <select
                id="paymentMethod"
                value={saleData.paymentMethod}
                onChange={e =>
                  setSaleData(prev => ({
                    ...prev,
                    paymentMethod: e.target.value as 'cash' | 'financing' | 'exchange',
                  }))
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="cash">À vista</option>
                <option value="financing">Financiamento</option>
                <option value="exchange">Troca</option>
              </select>
            </div>

            {/* Observações */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                id="notes"
                rows={4}
                value={saleData.notes || ''}
                onChange={e => setSaleData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Observações sobre a venda (opcional)"
              />
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/vehicles/${id}`)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={sellVehicleMutation.isLoading}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sellVehicleMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processando...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Confirmar Venda
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VehicleSale;
